import { useTranslation } from 'react-i18next';
import { Appointment } from '../../../../core/models/appointment';
import { AppointmentStatus } from '../../../../core/models/appointment-status.enum';
import { useState } from 'react';
import { deleteAppointment, updateAppointment } from '../../../../core/services/appointment.service';
import { sendMail } from '../../../../core/services/mail.service';
import { getFormattedClockDate } from '../../../../core/utils/date.utils';
import { Button, Tag } from 'antd';
import { SvgIcon } from '../../../../components/svg-icon/svg-icon';
import './appointment-card.scss';

type AppointmentCardProps = {
	appointment: Appointment;
	onChanged: (appointment: Appointment) => void;
	onDelete: (appointmentId: number) => void;
};

export const AppointmentCard: React.FC<AppointmentCardProps> = ({ appointment, onChanged, onDelete }) => {
	const { t } = useTranslation();

	const [appointmentStatus, setAppointmentStatus] = useState<AppointmentStatus>(appointment.status);

	function getTagColor(appointmentStatus: AppointmentStatus): string {
		switch (appointmentStatus) {
			case AppointmentStatus.ACCEPTED:
				return 'green';
			case AppointmentStatus.REJECTED:
				return 'red';
			default:
				return 'cyan';
		}
	}

	async function onHandleDeleteAppointment(): Promise<void> {
		if (!appointment.id) return;
		await deleteAppointment(appointment.id);
		onDelete(appointment.id);
	}

	function clickUpdateStatus(newAppointmentStatus: AppointmentStatus): void {
		setAppointmentStatus(newAppointmentStatus);

		const updatedAppointment: Appointment = {
			...appointment,
			status: newAppointmentStatus,
		};

		sendCustomerMail(updatedAppointment);
		updateAppointment(updatedAppointment);
		onChanged(updatedAppointment);
	}
	const sendCustomerMail = (appointment: Appointment) => {
		if (appointment.status === AppointmentStatus.ACCEPTED && window.bookrPluginData.notifications.sendCustomerAcceptedMail) {
			sendMail({
				to: appointment.customer?.email ?? '',
				subject: t('backEnd.mailTemplate.acceptAppointmentRequest.customer.subject', {
					appointmentStatus: t(`backEnd.appointment.${AppointmentStatus.ACCEPTED.toLowerCase()}`),
				}),
				body: t('backEnd.mailTemplate.acceptAppointmentRequest.customer.body', {
					startTime: getFormattedClockDate(appointment.startTime),
				}),
			});
		}

		if (appointment.status === AppointmentStatus.REJECTED && window.bookrPluginData.notifications.sendCustomerRejectedMail) {
			sendMail({
				to: appointment.customer?.email ?? '',
				subject: t('backEnd.mailTemplate.rejectAppointmentRequest.customer.subject', {
					appointmentStatus: t(`backEnd.appointment.${AppointmentStatus.REJECTED.toLowerCase()}`),
				}),
				body: t('backEnd.mailTemplate.rejectAppointmentRequest.customer.body', {
					startTime: getFormattedClockDate(appointment.startTime),
				}),
			});
		}
	};

	return (
		<div className='appointment-card'>
			<div className='appointment-card-details'>
				<span className='appointment-card-customer-name'>
					{appointment.customer?.firstname} {appointment.customer?.lastname}
				</span>
				<span>{getFormattedClockDate(new Date(appointment.startTime))}</span>
				<span>
					<Tag color={getTagColor(appointmentStatus)}>{t(`backEnd.appointment.${appointmentStatus.toLowerCase()}`)}</Tag>
				</span>
			</div>

			<div className='appointment-card-actions'>
				{appointmentStatus !== AppointmentStatus.ACCEPTED && (
					<Button type='primary' onClick={() => clickUpdateStatus(AppointmentStatus.ACCEPTED)}>
						{t('backEnd.appointment.accept')}
					</Button>
				)}
				{appointmentStatus !== AppointmentStatus.REJECTED && (
					<Button onClick={() => clickUpdateStatus(AppointmentStatus.REJECTED)}>{t('backEnd.appointment.reject')}</Button>
				)}
				{(appointmentStatus === AppointmentStatus.ACCEPTED || appointmentStatus === AppointmentStatus.REJECTED) && (
					<Button icon={<SvgIcon name='delete' size={16} />} onClick={async () => await onHandleDeleteAppointment()}>
						{t('common.delete')}
					</Button>
				)}
			</div>
		</div>
	);
};
